import React, { useContext, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { UptimeDowntimeBar } from "../UptimeDowntimeBar/UptimeDowntimeBar.view";
import { useStyles } from "./UptimeDowntimeBartList.styles";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { useParams } from "react-router-dom";
import { IEventFailureCodeSummaryPerMC } from "entities/Mills/MillsEntityInterfaces";
import NoData from "components/NoData/NoData.view";
import { GroupDataByProductionLine } from "./UptimeDowntimeBarListInterfaces";
import { SummaryReportDashboardContext } from "contexts/SummaryReportDashboardContext/SummaryReportDashboardContext";
import { initialMillFilters } from "../SummaryReportFilters/useMillFilters";

export const UptimeDowntimeBartList = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { filters } = useContext(SummaryReportDashboardContext);
    const { millId } = useParams();
    const queryFilters = useMemo(
        () => ({
            ...filters,
            startDate: filters.startDate ? filters.startDate : initialMillFilters.startDate,
            endDate: filters.endDate ? filters.endDate : initialMillFilters.endDate,
        }),
        [filters],
    );
    const { millUptimesVsDowntimes } = MillsEntity.GetMillUptimesVsDowntimes(Number(millId), queryFilters);

    const groupedDataByProductionLine: GroupDataByProductionLine = useMemo(
        () =>
            millUptimesVsDowntimes.reduce((groups: GroupDataByProductionLine, obj: IEventFailureCodeSummaryPerMC) => {
                const productionLineId = obj.machine_center.production_line.production_line_id;
                const productionLineName = obj.machine_center.production_line.name;
                const failureTypeId = obj.failure_type ? obj.failure_type.failure_type_id : null;

                if (!groups[productionLineId]) {
                    groups[productionLineId] = {
                        productionLineName: productionLineName,
                        downtime: {
                            seconds: 0,
                            occurrences: 0,
                        },
                        uptime: {
                            seconds: 0,
                            occurrences: 0,
                        },
                    };
                }

                if (failureTypeId !== null) {
                    groups[productionLineId].downtime.seconds += obj.seconds;
                    groups[productionLineId].downtime.occurrences += obj.occurrences;
                } else {
                    groups[productionLineId].uptime.seconds += obj.seconds;
                    groups[productionLineId].uptime.occurrences += obj.occurrences;
                }

                return groups;
            }, {}),
        [millUptimesVsDowntimes],
    );

    return (
        <>
            <Box className={classes.container} sx={{ flexDirection: "column" }}>
                <Box className={classes.titleContainer}>
                    <Typography className={classes.title} variant="subtitle1">
                        {t("locations.summary_report.uptime_vs_downtime.title")}
                    </Typography>
                </Box>
                <Box className={classes.infoContainer}>
                    {Object.values(groupedDataByProductionLine).length < 1 && (
                        <Box className={classes.noData}>
                            <NoData />
                        </Box>
                    )}
                    {Object.values(groupedDataByProductionLine).length >= 1 &&
                        Object.values(groupedDataByProductionLine).map((machineCenter, index) => (
                            <UptimeDowntimeBar
                                key={`${index + Math.random()}`}
                                productionLineName={machineCenter.productionLineName}
                                uptimeValue={machineCenter.uptime.seconds / 60}
                                downtimeValue={machineCenter.downtime.seconds / 60}
                            />
                        ))}
                </Box>
            </Box>
        </>
    );
};
